import React from 'react';
import { Plus, Trash2 } from 'lucide-react';
import { RecoveryProtocol, RecoveryItem } from '../../../types/program';

interface RecoveryProtocolTabProps {
  recoveryProtocol: RecoveryProtocol | null;
  onUpdate: (protocol: RecoveryProtocol) => void;
}

const RecoveryProtocolTab: React.FC<RecoveryProtocolTabProps> = ({ recoveryProtocol, onUpdate }) => {
  const addItem = () => {
    const newItem: RecoveryItem = {
      title: 'New Recovery Item',
      description: '',
      duration: 10,
      order: recoveryProtocol?.items.length || 0,
    };

    onUpdate({
      ...recoveryProtocol!,
      items: [...(recoveryProtocol?.items || []), newItem],
    });
  };

  const updateItem = (index: number, updatedItem: RecoveryItem) => {
    const updatedItems = (recoveryProtocol?.items || []).map((item, i) =>
      i === index ? updatedItem : item
    );

    onUpdate({
      ...recoveryProtocol!,
      items: updatedItems,
    });
  };

  const deleteItem = (index: number) => {
    const updatedItems = (recoveryProtocol?.items || []).filter((_, i) => i !== index);

    onUpdate({
      ...recoveryProtocol!,
      items: updatedItems,
    });
  };

  return (
    <div className="space-y-6">
      {/* Recovery Items */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold text-white">Recovery Items</h3>
          <button
            onClick={addItem}
            className="inline-flex items-center gap-2 px-4 py-2 bg-[#23F47C] text-[#0A0A0A] text-sm font-semibold rounded-lg hover:bg-[#1FD66D] transition-all"
          >
            <Plus size={16} />
            <span>Add Recovery Item</span>
          </button>
        </div>

        {(recoveryProtocol?.items || []).map((item, index) => (
          <div key={index} className="bg-[#141414] rounded-xl border border-[#1F1F1F] p-6">
            <div className="flex items-center justify-between mb-4">
              <span className="text-sm font-medium text-[#23F47C]">Item {index + 1}</span>
              <button
                onClick={() => deleteItem(index)}
                className="text-[#FF4757] hover:text-[#FF6B7A] p-2 hover:bg-[rgba(255,71,87,0.1)] rounded-lg transition-all"
              >
                <Trash2 size={16} />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-[#8F8F8F] mb-2">Title</label>
                <input
                  type="text"
                  value={item.title}
                  onChange={(e) =>
                    updateItem(index, { ...item, title: e.target.value })
                  }
                  className="w-full px-4 py-2 bg-[#0A0A0A] border border-[#1F1F1F] rounded-lg text-white focus:border-[#23F47C] focus:outline-none"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-[#8F8F8F] mb-2">Description</label>
                <textarea
                  value={item.description || ''}
                  onChange={(e) =>
                    updateItem(index, { ...item, description: e.target.value })
                  }
                  rows={2}
                  className="w-full px-4 py-2 bg-[#0A0A0A] border border-[#1F1F1F] rounded-lg text-white focus:border-[#23F47C] focus:outline-none"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-[#8F8F8F] mb-2">Duration (minutes)</label>
                  <input
                    type="number"
                    value={item.duration}
                    onChange={(e) =>
                      updateItem(index, { ...item, duration: parseInt(e.target.value) || 10 })
                    }
                    min="1"
                    className="w-full px-4 py-2 bg-[#0A0A0A] border border-[#1F1F1F] rounded-lg text-white focus:border-[#23F47C] focus:outline-none"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#8F8F8F] mb-2">Video URL (optional)</label>
                  <input
                    type="url"
                    value={item.videoUrl || ''}
                    onChange={(e) =>
                      updateItem(index, { ...item, videoUrl: e.target.value })
                    }
                    placeholder="https://youtube.com/watch?v=..."
                    className="w-full px-4 py-2 bg-[#0A0A0A] border border-[#1F1F1F] rounded-lg text-white focus:border-[#23F47C] focus:outline-none"
                  />
                </div>
              </div>
            </div>
          </div>
        ))}

        {(recoveryProtocol?.items || []).length === 0 && (
          <div className="text-center py-8 text-[#8F8F8F]">
            No recovery items yet. Add your first recovery item above.
          </div>
        )}
      </div>
    </div>
  );
};

export default RecoveryProtocolTab;
